/**
 * Shortcut functions to mimic the browser methods of matching names have been created to
 * simplify usage. These all take an additional final argument of `options` which can be
 * used to override the internal Modal.Dialog options.
 *
 * Additionally, they return an extended promise. It is extended to also include a
 * `dialog` property to interface with the created dialog directly if required.
 *
 * By default the text argument(s) are escaped to avoid XSS vulnerabilities.
 * Although passing `allowHtml: true` as an option will avoid this escapement.
 */
import _ from 'underscore';
import $ from 'jquery';
import Dialog from './index';

var cleanInput = function cleanInput(str) {
  var text = $('<div></div>').text(str).html();
  return text.replace(/\n/g, '<br>');
};

var getSafeHtml = function getSafeHtml(str, options) {
  var html = options.allowHtml ? str : cleanInput(str);

  if (options.simpleTitle) {
    html = "<h3>" + cleanInput(options.simpleTitle) + "</h3>" + html;
  }

  return html;
};

var promisedDialog = function promisedDialog(str, rejectOnClose, options) {
  var deferred = $.Deferred();
  var html = getSafeHtml(str, options);
  options = $.extend(true, {
    size: 'small',
    content: html
  }, options);
  var dialog = new Dialog(options);
  dialog.once('click:primary', function () {
    return deferred.resolve();
  });
  dialog.once('close', function () {
    if (rejectOnClose) {
      return deferred.reject();
    } else {
      return deferred.resolve();
    }
  });
  deferred.always(function () {
    return dialog.remove();
  });
  dialog.open();
  return _.extend(deferred.promise(), {
    dialog: dialog
  });
};
/*
 * Similar to window.alert()
 *
 * @example
 *   PollEv.Component.Modal.alert "Thank <strong>you</strong> for signing up!"
 *     allowHtml: true
 *
 * @param {String} str Alert text
 * @param {Object} options Dialog options
 * @return {Promise}
 */


var alert = function alert(str, options) {
  if (options == null) {
    options = {};
  }

  return promisedDialog(str, false, options);
};
/**
 * Similar to window.confirm() but returns a promise
 *
 * @example
 *   PollEv.Component.Modal.confirm "Are you sure you want to delete these items?
 *     simpleTitle: "Delete Items"
 *   .then (val) ->
 *     console.log "DELETING THEM ALL"
 *
 * @param {String} str Confirmation text
 * @param {Object} options Dialog options
 * @return {Promise}
 */


var confirm = function confirm(str, options) {
  if (options == null) {
    options = {};
  }

  options = $.extend(true, {
    actions: {
      cancel: 'Cancel'
    }
  }, options);
  return promisedDialog(str, true, options);
};
/*
 * Similar to window.prompt() but returns a promise
 *
 * @example
 *   PollEv.Component.Modal.prompt "What would you like for lunch?", "Banana"
 *     .then (val) ->
 *       console.log "Okay, sure, here's a #{val}"
 *     .fail ->
 *       console.error "Why nothing?"
 *
 * @param {String} str Prompt text
 * @param {String} initial Placeholder text
 * @param {Object} options Dialog options
 * @param {RegExp|Function(value)} options.validator Disables submission if evaluates to false
 * @param {String} options.placeholder Input placeholder text
 * @return {Promise}
 */


var prompt = function prompt(str, initial, options) {
  if (initial === void 0) {
    initial = '';
  }

  if (options === void 0) {
    options = {};
  }

  var html = getSafeHtml(str, options);
  html += '<div class="modal-dialog--prompt"><input></div>';
  var deferred = $.Deferred();
  options = $.extend(true, {
    size: 'small',
    maxLength: 0,
    content: html,
    actions: {
      cancel: 'Cancel'
    }
  }, options);
  var dialog = new Dialog(options);
  var input = dialog.$el.find('.modal-dialog--prompt input');
  var primary = dialog.$el.find('.modal-dialog--primary');
  input.attr('value', cleanInput(initial));

  if (options.placeholder) {
    input.attr('placeholder', options.placeholder);
  }

  if (options.maxLength) {
    input.attr('maxlength', options.maxLength);
  }

  if (options.validator) {
    var validate = function validate() {
      var isValid;

      if (options.validator instanceof RegExp) {
        isValid = options.validator.test(input.val());
      } else {
        isValid = options.validator(input.val());
      }

      input.toggleClass('s-invalid', !isValid);
      primary.prop('disabled', !isValid);
      return isValid;
    };

    var okClick = function okClick() {
      if (validate()) {
        return deferred.resolve(input.val());
      }
    };

    dialog.once('open', function () {
      return input.on('change keyup', validate);
    });
    dialog.on('click:primary', okClick);
    dialog.once('close', function () {
      return dialog.off('click:primary', okClick);
    });
  } else {
    dialog.once('click:primary', function () {
      return deferred.resolve(input.val());
    });
  }

  dialog.once('close', function () {
    return deferred.reject();
  });
  deferred.always(function () {
    return dialog.remove();
  });
  dialog.open();
  input.focus()[0].setSelectionRange(initial.length, initial.length);
  input.on('keyup', function (e) {
    if (e.which === 13 && !input.is('[disabled]')) {
      return primary.click();
    }
  });
  return _.extend(deferred.promise(), {
    dialog: dialog
  });
};

export { alert, confirm, prompt };